/**
 * components/procurement/BudgetCopilotPanel.jsx — "Beacon" budget strategist chat.
 *
 * Beacon is your personal budget strategist (distinct from the org-wide AI Agent)
 * that knows your data — properties, meters, bills, contracts & market feeds —
 * and can discuss, model, build budgets & add projected ("new project")
 * properties conversationally. Each message meters 1 AI credit.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Send, ArrowRight, Radar, Check, X } from 'lucide-react';

import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '../ui/sheet';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { BeaconShimmer } from './BeaconShimmer';
import { agentChat, applyAgentAction, getAgentHistory } from '../../api/budgets';

const SUGGESTIONS = [
  'Build a 5-year electricity budget for my top 3 properties',
  'Add a new project property cloned from my biggest site with 2× the meters',
  'Suggest a hedging scenario using my contracts',
];

const newSession = () => `beacon-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const ACTION_LABEL = {
  create_budget: 'Create budget',
  update_scenario: 'Update scenario',
  create_scenario: 'Create scenario',
  add_projected_property: 'Add new-project property',
};

export function BudgetCopilotPanel({ open, onOpenChange, accountId, budgetId, onChanged }) {
  const navigate = useNavigate();
  const [sessionId] = useState(newSession);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [pending, setPending] = useState(null); // {action} awaiting confirm
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    getAgentHistory({ accountId, sessionId }).then((d) => setMessages(d.messages || [])).catch(() => {});
  }, [open, accountId, sessionId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending, pending]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput('');
    setPending(null);
    setMessages((m) => [...m, { role: 'user', content: msg }]);
    setSending(true);
    try {
      const res = await agentChat({ accountId, sessionId, message: msg, currentBudgetId: budgetId });
      setMessages((m) => [...m, { role: 'assistant', content: res.reply, action_result: res.action_result }]);
      if (res.pending_action) setPending({ action: res.pending_action });
      if (res.action_result) onChanged?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      setMessages((m) => [...m, { role: 'assistant', content: `⚠️ ${detail}` }]);
      toast.error('Beacon error', { description: detail });
    } finally {
      setSending(false);
    }
  };

  const confirmPending = async () => {
    if (!pending || sending) return;
    setSending(true);
    try {
      const res = await applyAgentAction({ accountId, sessionId, action: pending.action, currentBudgetId: budgetId });
      setMessages((m) => [...m, { role: 'assistant', content: res.reply, action_result: res.action_result }]);
      setPending(null);
      onChanged?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast.error('Could not apply', { description: detail });
    } finally {
      setSending(false);
    }
  };

  const cancelPending = () => {
    setPending(null);
    setMessages((m) => [...m, { role: 'assistant', content: 'Okay — I won\'t make that change.' }]);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:w-[40vw] sm:max-w-none min-w-[400px] flex flex-col p-0"
        data-testid="copilot-panel"
      >
        <SheetHeader className="p-5 border-b">
          <SheetTitle className="flex items-center gap-2" data-testid="beacon-title">
            <span className="w-7 h-7 rounded-md bg-violet-600 flex items-center justify-center">
              <Radar className="w-4 h-4 text-white" />
            </span>
            Beacon
          </SheetTitle>
          <SheetDescription>
            Your personal budget strategist — knows your properties, bills, contracts &amp;
            market feeds. Discuss, model, build budgets, or add a new-project property.
            (1 credit per message)
          </SheetDescription>
        </SheetHeader>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="copilot-messages">
          {messages.length === 0 && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Try asking:</p>
              {SUGGESTIONS.map((s) => (
                <button key={s} onClick={() => send(s)} className="block w-full text-left text-sm border rounded-md px-3 py-2 hover:bg-muted/50 transition-colors"
                  data-testid="copilot-suggestion">
                  {s}
                </button>
              ))}
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] rounded-lg px-3.5 py-2.5 text-sm whitespace-pre-line ${
                m.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}
                data-testid={`copilot-msg-${m.role}`}>
                {m.content}
                {m.action_result?.budget_id && (
                  <button onClick={() => { navigate(`/procurement/budgets/${m.action_result.budget_id}`); onOpenChange(false); }}
                    className="mt-2 flex items-center gap-1 text-xs font-medium underline" data-testid="copilot-open-budget">
                    Open {m.action_result.name || 'budget'} <ArrowRight className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="bg-muted rounded-lg px-3.5 py-2.5">
                <BeaconShimmer />
              </div>
            </div>
          )}
          {pending && !sending && (
            <div className="border-2 border-violet-300 bg-violet-50/60 rounded-lg p-3" data-testid="beacon-confirm-card">
              <p className="text-xs font-semibold text-violet-700 uppercase tracking-wide mb-2">
                Confirm change · {ACTION_LABEL[pending.action.type] || pending.action.type}
              </p>
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={confirmPending} className="bg-violet-600 hover:bg-violet-700" data-testid="beacon-confirm-btn">
                  <Check className="w-3.5 h-3.5 mr-1" /> Confirm
                </Button>
                <Button size="sm" variant="outline" onClick={cancelPending} data-testid="beacon-cancel-btn">
                  <X className="w-3.5 h-3.5 mr-1" /> Cancel
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t flex items-center gap-2">
          <Input value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
            placeholder="Ask Beacon or tell it what to model…" disabled={sending}
            data-testid="copilot-input" />
          <Button onClick={() => send()} disabled={sending || !input.trim()} size="icon" data-testid="copilot-send">
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
